/**
 * Role-Based Access Control (RBAC) Helpers
 * Provides functions to check user permissions and roles for project management
 */

import { getRolePermissions } from '../constants/roles'
export { getRolePermissions }

/**
 * Get current user's role for a project
 * @param {Object} store - Vuex store instance
 * @param {string} projectId - Project ID
 * @returns {string|null} User's role or null if not found
 */
export function current_role(store, projectId) {
  if (!store || !projectId) return null

  const currentUser = store.state.auth?.user
  if (!currentUser) return null

  // First, try to get role from user's projects array (available immediately after login)
  const userProjects = currentUser.projects || []
  const userProject = userProjects.find(p => p._id === projectId)
  if (userProject?.role) {
    return userProject.role
  }

  // Fallback to members data if available (for when members are loaded for a specific project)
  const members = store.state.usersSubmission?.members || []
  const currentMember = members.find(m => m._id === currentUser.id)

  if (!currentMember) return 'View Only'

  const projectRole = currentMember.projectRole?.find(
    role => role.project === projectId
  )

  if (projectRole) {
    return projectRole.role
  }

  // If user exists but has no role for this project, default to Administrator
  return 'Administrator'
}

/**
 * Check if user can edit project
 * @param {Object} store - Vuex store instance
 * @param {string} projectId - Project ID
 * @returns {boolean} True if user can edit
 */
export function canEdit(store, projectId) {
  const role = current_role(store, projectId)
  if (!role) return false

  const editableRoles = ['Administrator', 'Reader']
  return editableRoles.includes(role)
}

/**
 * Check if user can upload to project
 * @param {Object} store - Vuex store instance
 * @param {string} projectId - Project ID
 * @returns {boolean} True if user can upload
 */
export function canUpload(store, projectId) {
  const role = current_role(store, projectId)
  if (!role) return false

  const uploadRoles = ['Administrator', 'Data Upload']
  return uploadRoles.includes(role)
}

/**
 * Check if user is admin for project
 * @param {Object} store - Vuex store instance
 * @param {string} projectId - Project ID
 * @returns {boolean} True if user is admin
 */
export function isAdmin(store, projectId) {
  const role = current_role(store, projectId)
  return role === 'Administrator'
}

/**
 * Check if user can view project
 * @param {Object} store - Vuex store instance
 * @param {string} projectId - Project ID
 * @returns {boolean} True if user can view
 */
export function canView(store, projectId) {
  const role = current_role(store, projectId)
  return role !== null
}

/**
 * Check if user can manage members
 * @param {Object} store - Vuex store instance
 * @param {string} projectId - Project ID
 * @returns {boolean} True if user can manage members
 */
export function canManageMembers(store, projectId) {
  return isAdmin(store, projectId)
}

/**
 * Check if user can delete project
 * @param {Object} store - Vuex store instance
 * @param {string} projectId - Project ID
 * @returns {boolean} True if user can delete
 */
export function canDeleteProject(store, projectId) {
  return isAdmin(store, projectId)
}

/**
 * Check if user can label/annotate images
 * @param {Object} store - Vuex store instance
 * @param {string} projectId - Project ID
 * @returns {boolean} True if user can label
 */
export function canLabel(store, projectId) {
  const role = current_role(store, projectId)
  if (!role) return false

  const labelRoles = ['Reader', 'Moderator']
  return labelRoles.includes(role)
}


